import type { PostHog } from "posthog-js";
import * as React from "react";
import {
  Bookmark01Icon,
  Comment01Icon,
  CursorMagicSelection01Icon,
  Edit02Icon,
  FavouriteIcon,
  Message01Icon,
  MouseScroll01Icon,
  News01Icon,
  Search01Icon,
  ThumbsUpIcon,
  UserAdd01Icon,
  UserSearch01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link } from "@tanstack/react-router";
import { CheckIcon, CopyIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { usePostHog } from "posthog-js/react";

import type { OsKey } from "../osInstall";
import type { ProviderKey } from "./SetupConsole";
import type { IconSvgElement } from "@hugeicons/react";
import { cn } from "@/common/utils";
import { Button } from "@/components/ui/Button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import {
  checkIsConnectorUser,
  deriveConnectorProvider,
} from "../connectorAudience";
import {
  detectDefaultOs,
  OS_INSTRUCTIONS,
  OS_OPTIONS,
  PREREQ_COMMANDS,
} from "../osInstall";
import { AntidetectGraph } from "./AntidetectGraph";
import { SetupConsole } from "./SetupConsole";

// Hairline card chrome on the light onboarding surface (replaces the dark
// GLASS_STYLE/GLASS_POPULAR films, which read as a white haze on --ground).
const HAIRLINE_STYLE = {
  borderColor: "var(--hair)",
  backgroundColor: "var(--ground)",
} as const;

export function GettingStartedStep({
  variant = "default",
  tools = [],
  copiedField,
  isPending,
  isInstallPrereqs,
  currentStepKey,
  onStepChange,
  onCopy,
  onComplete,
  onBack,
}: {
  variant?: "default" | "linkedin";
  tools?: string[];
  copiedField: string | null;
  isPending: boolean;
  isInstallPrereqs: boolean;
  currentStepKey: string;
  onStepChange: (nextStepKey: string) => void;
  onCopy: (text: string, field: string) => void;
  onComplete: () => void;
  onBack: () => void;
}) {
  const posthog = usePostHog();
  const welcomeSubtitle =
    variant === "linkedin" ? WELCOME_SUBTITLE_LINKEDIN : WELCOME_SUBTITLE;
  const memoryPlatforms =
    variant === "linkedin" ? "LinkedIn" : "LinkedIn, X, and Reddit";
  const stepNum = (num: number) => (isInstallPrereqs ? num + 1 : num);
  // Always start on "macos" for SSR + first client render so the rendered
  // tab matches the server HTML; a post-mount effect upgrades to the
  // navigator-derived default once we're safely on the client.
  const [selectedOs, setSelectedOs] = React.useState<OsKey>("macos");
  React.useEffect(() => {
    setSelectedOs(detectDefaultOs());
  }, []);
  const lastTriggerRef = React.useRef<"initial" | "next" | "previous" | "skip">(
    "initial",
  );

  // ChatGPT joins Cowork on the connector path: it has no CLI at all, so the
  // MCP tab is the only way in. Codex deliberately stays on the CLI path,
  // where it is pointed at the connector as an alternative rather than
  // redirected: it can use both, and the CLI is the richer surface.
  const isConnectorUser = checkIsConnectorUser(tools);
  const [selectedPath, setSelectedPath] = React.useState<SetupPath>(
    isConnectorUser ? "cowork" : "cli",
  );
  // Welcome answers load async, so the initial state can be computed from an
  // empty tools array. Follow the derived default until the user picks a tab
  // themselves.
  const hasUserChosenPathRef = React.useRef(false);
  React.useEffect(() => {
    if (hasUserChosenPathRef.current) return;
    setSelectedPath(isConnectorUser ? "cowork" : "cli");
  }, [isConnectorUser]);
  const connectorProvider = deriveConnectorProvider(tools);

  const cliSteps = (
    <>
      <StepCodingAgent />
      {isInstallPrereqs && (
        <StepBasics
          copiedField={copiedField}
          onCopy={onCopy}
          posthog={posthog}
          selectedOs={selectedOs}
          setSelectedOs={setSelectedOs}
        />
      )}
      <StepInstall
        stepNum={stepNum}
        copiedField={copiedField}
        onCopy={onCopy}
      />
    </>
  );

  const steps: { key: string; node: React.ReactNode }[] = [
    {
      key: "setup",
      node: (
        <Tabs
          value={selectedPath}
          onValueChange={(value) => {
            hasUserChosenPathRef.current = true;
            setSelectedPath(value as SetupPath);
          }}
        >
          <TabsList className="flex h-auto w-full gap-1 rounded-lg border border-[var(--cli-border)] bg-[var(--cli-bg-elevated)] p-1">
            <TabsTrigger value="cli" className={PATH_TAB_STYLE}>
              Wonda CLI
            </TabsTrigger>
            <TabsTrigger value="cowork" className={PATH_TAB_STYLE}>
              Wonda MCP & Browser
            </TabsTrigger>
          </TabsList>
          <TabsContent value="cli" className="mt-6 space-y-10">
            {cliSteps}
            {/* The mirror of the cowork tab's "Prefer the terminal?" link.
                Codex and Claude Code can drive Wonda either way, so once the
                CLI guide is done, offer the connector rather than leaving it
                undiscoverable behind a tab they were defaulted away from. */}
            <p className="text-sm text-[var(--cli-text-muted)]">
              Rather connect it to your AI app?{" "}
              <button
                type="button"
                onClick={() => {
                  hasUserChosenPathRef.current = true;
                  setSelectedPath("cowork");
                }}
                className="cursor-pointer font-medium text-[var(--cli-text)] underline underline-offset-4"
              >
                Set up the Wonda MCP connector instead
              </button>
            </p>
          </TabsContent>
          <TabsContent value="cowork" className="mt-6 space-y-6">
            <StepCowork defaultProvider={connectorProvider} />
            <p className="text-sm text-[var(--cli-text-muted)]">
              Prefer the terminal?{" "}
              <button
                type="button"
                onClick={() => {
                  hasUserChosenPathRef.current = true;
                  setSelectedPath("cli");
                }}
                className="cursor-pointer font-medium text-[var(--cli-text)] underline underline-offset-4"
              >
                Set up the Wonda CLI instead
              </button>
            </p>
          </TabsContent>
        </Tabs>
      ),
    },
  ];
  steps.push({ key: "capabilities", node: <StepCapabilities /> });
  steps.push({
    key: "complete",
    node: (
      <StepComplete
        copiedField={copiedField}
        onCopy={onCopy}
        onComplete={onComplete}
        isPending={isPending}
        memoryPlatforms={memoryPlatforms}
      />
    ),
  });
  const currentStepIndex = Math.max(
    0,
    steps.findIndex((step) => step.key === currentStepKey),
  );
  const safeCurrentStep = Math.min(currentStepIndex, steps.length - 1);
  const isLastStep = safeCurrentStep === steps.length - 1;
  const activeStep = steps[safeCurrentStep] ?? steps[0];

  React.useEffect(() => {
    const step = steps[safeCurrentStep];
    if (!step) return;
    posthog.capture("welcome guide step viewed", {
      stepKey: step.key,
      stepIndex: safeCurrentStep,
      totalSteps: steps.length,
      trigger: lastTriggerRef.current,
    });
    // `steps` is rebuilt every render; depending on it would loop. `steps.length`
    // is the only structural change that matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeCurrentStep, steps.length, posthog]);

  if (!activeStep) return null;

  return (
    <div className="flex min-h-[calc(100dvh-var(--header-height,80px))]">
      {/* Left panel: getting started */}
      <div className="flex min-w-0 flex-1 flex-col px-6 sm:px-12 lg:px-16">
        <div className="mx-auto flex w-full max-w-lg min-w-0 flex-1 flex-col">
          {/* Top row — sticky */}
          <div className="flex items-center justify-between py-4">
            <Button variant="ghost" size="default" onClick={onBack}>
              ← Back
            </Button>
            {!isLastStep && (
              <button
                type="button"
                onClick={() => {
                  const lastStep = steps[steps.length - 1];
                  if (!lastStep) return;
                  lastTriggerRef.current = "skip";
                  onStepChange(lastStep.key);
                }}
                className="cursor-pointer text-sm text-[var(--cli-text-muted)] transition-colors hover:text-[var(--cli-text)]"
              >
                Skip →
              </button>
            )}
          </div>

          {/* Middle row — natural page scroll */}
          <div className="flex-1 space-y-10 py-6">
            {safeCurrentStep === 0 && (
              <div>
                <h1 className="text-2xl font-bold text-[var(--cli-text)]">
                  Welcome to Wonda
                </h1>
                <p className="mt-2 text-sm text-[var(--cli-text-muted)]">
                  {welcomeSubtitle}
                </p>
              </div>
            )}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep.key}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                {activeStep.node}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Bottom row — sticky footer */}
          <div className="sticky bottom-0 z-10 bg-[var(--cli-bg)] pt-8 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between gap-3">
              <span className="hidden text-sm text-[var(--cli-text-muted)] sm:inline">
                Step {safeCurrentStep + 1} of {steps.length}
              </span>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="default"
                  onClick={() => {
                    const previousStep =
                      steps[Math.max(0, safeCurrentStep - 1)];
                    if (!previousStep) return;
                    lastTriggerRef.current = "previous";
                    onStepChange(previousStep.key);
                  }}
                  disabled={safeCurrentStep === 0}
                  className={safeCurrentStep === 0 ? "invisible" : undefined}
                >
                  ← Previous
                </Button>
                <Button
                  variant="steward"
                  size="default"
                  onClick={() => {
                    const nextStep =
                      steps[Math.min(steps.length - 1, safeCurrentStep + 1)];
                    if (!nextStep) return;
                    lastTriggerRef.current = "next";
                    onStepChange(nextStep.key);
                  }}
                  className={isLastStep ? "invisible" : undefined}
                  disabled={isLastStep}
                >
                  Next →
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel: antidetect graph (desktop only) */}
      <div className="hidden flex-col items-center justify-center gap-8 border-l border-[var(--cli-border)] bg-[var(--cli-bg-subtle)] lg:sticky lg:top-[var(--header-height,80px)] lg:flex lg:h-[calc(100dvh-var(--header-height,80px))] lg:w-2/5 lg:self-start xl:w-1/2">
        <AntidetectGraph variant={variant} />
      </div>
    </div>
  );
}

// The card used to be Claude-only, hence the Claude mark and the "For Claude
// Cowork" heading. The connector now serves Claude, ChatGPT and Codex from one
// URL, so the heading names all three and the mark gives way to the per-
// provider logos the picker inside already shows. The tab this sits under was
// always called "Wonda MCP & Browser", so nothing above it needed renaming.
function StepCowork({ defaultProvider }: { defaultProvider: ProviderKey }) {
  return (
    <section className="rounded-xl border p-5" style={HAIRLINE_STYLE}>
      <div className="flex items-center gap-2">
        {CONNECTOR_LOGOS.map((logo) => (
          <img
            key={logo.src}
            src={logo.src}
            alt=""
            // Both marks ship white-fill, invisible on the light Steward
            // ground, so invert to ink (matches the agent logos on the CLI tab).
            className="size-5 shrink-0 [filter:brightness(0)]"
          />
        ))}
        <p className="text-base font-semibold text-[var(--cli-text)]">
          For Claude, ChatGPT and Codex
        </p>
      </div>
      <p className="mt-1 text-sm text-[var(--cli-text-muted)]">
        No terminal needed. Three steps, and the checkmarks tick themselves off
        as you go.
      </p>
      <div className="mt-4">
        <SetupConsole variant="guide" defaultProvider={defaultProvider} />
      </div>
    </section>
  );
}

function StepCodingAgent() {
  return (
    <section className="space-y-4">
      <p className="text-base font-medium text-[var(--cli-text-secondary)]">
        1. Make sure you have a coding agent
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {AGENT_LOGOS.map((agent) => (
          <a
            key={agent.label}
            href={agent.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-2 overflow-hidden rounded-xl border px-4 py-3 transition-colors hover:bg-[var(--cli-bg-hover)]"
            style={HAIRLINE_STYLE}
          >
            <img
              src={agent.icon}
              alt=""
              // The agent marks ship white-fill (SVGs + the claude-code-bot
              // PNG), invisible on the light Steward ground, so invert to ink.
              className="size-7 [filter:brightness(0)]"
            />
            <span className="text-xs text-[var(--cli-text-muted)]">
              {agent.label}
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function StepBasics({
  copiedField,
  onCopy,
  posthog,
  selectedOs,
  setSelectedOs,
}: {
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
  posthog: PostHog;
  selectedOs: OsKey;
  setSelectedOs: (os: OsKey) => void;
}) {
  return (
    <section className="space-y-4">
      <div>
        <p className="text-base font-medium text-[var(--cli-text-secondary)]">
          2. Get the basics
        </p>
        <HelperText className="mt-1">
          Wonda needs Node, git, and ffmpeg on your machine.
        </HelperText>
      </div>
      <Tabs
        value={selectedOs}
        onValueChange={(value) => {
          setSelectedOs(value as OsKey);
          posthog.capture("welcome guide prereq os changed", {
            os: value,
          });
        }}
      >
        <TabsList className="flex h-auto w-full gap-1 rounded-lg border border-[var(--cli-border)] bg-[var(--cli-bg-elevated)] p-1">
          {OS_OPTIONS.map((option) => (
            <TabsTrigger
              key={option.value}
              value={option.value}
              className="flex-1 rounded-md px-3 py-1.5 text-xs leading-normal font-medium text-[var(--cli-text-muted)] transition-colors hover:text-[var(--cli-text)] data-[state=active]:bg-[var(--cli-bg-hover)] data-[state=active]:text-[var(--cli-text)] data-[state=active]:shadow-none"
            >
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <HelperText>{OS_INSTRUCTIONS[selectedOs]}</HelperText>
      <div className="space-y-2">
        {PREREQ_COMMANDS[selectedOs].map((item, index) => {
          const field = `prereq-${selectedOs}-${index}`;
          const isCopied = copiedField === field;
          return (
            <button
              key={item.label}
              type="button"
              onClick={() => {
                onCopy(item.command, field);
                posthog.capture("welcome guide prereq copied", {
                  os: selectedOs,
                  label: item.label,
                });
              }}
              aria-label={`Copy "${item.label}" command`}
              className="group relative block w-full rounded-xl bg-[var(--cli-bg-elevated)] p-3 pr-10 text-left transition-colors hover:bg-[var(--cli-bg-hover)]"
            >
              <span className="block text-xs font-medium text-[var(--cli-text-secondary)]">
                {item.label}
              </span>
              <code className="mt-1.5 block truncate text-xs text-[var(--cli-text-muted)]">
                {item.command}
              </code>
              <span className="absolute top-3 right-3 text-[var(--cli-text-muted)] opacity-50 transition-opacity group-hover:text-[var(--cli-text)] group-hover:opacity-100">
                {isCopied ? (
                  <CheckIcon className="size-4" strokeWidth={1.5} />
                ) : (
                  <CopyIcon className="size-4" strokeWidth={1.5} />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function StepInstall({
  stepNum,
  copiedField,
  onCopy,
}: {
  stepNum: (num: number) => number;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
}) {
  return (
    <section className="space-y-3">
      <p className="text-base font-medium text-[var(--cli-text-secondary)]">
        {stepNum(2)}. Ask your agent to install Wonda
      </p>
      <button
        type="button"
        onClick={() => onCopy(AGENT_INSTALL_PROMPT, "agentInstall")}
        aria-label="Copy install prompt"
        className="group relative w-full rounded-xl bg-[var(--cli-bg-elevated)] px-4 py-3 pr-10 text-left transition-colors hover:bg-[var(--cli-bg-hover)]"
      >
        <span className="text-sm leading-relaxed text-[var(--cli-text-secondary)]">
          {AGENT_INSTALL_PROMPT}
        </span>
        <span className="absolute top-3 right-3 text-[var(--cli-text-muted)] opacity-50 transition-opacity group-hover:text-[var(--cli-text)] group-hover:opacity-100">
          {copiedField === "agentInstall" ? (
            <CheckIcon className="size-4" strokeWidth={1.5} />
          ) : (
            <CopyIcon className="size-4" strokeWidth={1.5} />
          )}
        </span>
      </button>
      <p className="text-xs text-[var(--cli-text-muted)]">
        or install it yourself: your one-line terminal command is on the{" "}
        <Link to="/download" className="underline underline-offset-4">
          download page
        </Link>
        .
      </p>
    </section>
  );
}

function StepCapabilities() {
  return (
    <section className="space-y-4">
      <p className="text-base font-medium text-[var(--cli-text)]">
        Ask your Claude to do anything you&apos;re doing manually: research
        people, look up who commented on posts, send connection requests, even
        scroll &amp; engage on posts from your team or ICP
      </p>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-[var(--hair)] bg-[var(--hair)] sm:grid-cols-3">
        {CAPABILITIES.map((cap) => (
          <div
            key={cap.label}
            className="flex items-center gap-2.5 bg-[var(--cli-bg-elevated)] px-3.5 py-3"
          >
            <span
              className="flex size-8 shrink-0 items-center justify-center rounded-full"
              style={{
                background: "color-mix(in srgb, var(--green) 12%, transparent)",
              }}
            >
              <HugeiconsIcon
                icon={cap.icon}
                className="size-4 text-[var(--green)]"
                strokeWidth={1.7}
              />
            </span>
            <span className="text-[13px] font-medium tracking-tight text-[var(--cli-text)]">
              {cap.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function StepComplete({
  copiedField,
  onCopy,
  onComplete,
  isPending,
  memoryPlatforms,
}: {
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
  onComplete: () => void;
  isPending: boolean;
  memoryPlatforms: string;
}) {
  return (
    <section className="space-y-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <span
          className="flex size-12 items-center justify-center rounded-full"
          style={{
            background: "color-mix(in srgb, var(--green) 12%, transparent)",
          }}
        >
          <CheckIcon className="size-6 text-[var(--green)]" strokeWidth={2} />
        </span>
        <div>
          <p className="text-lg font-semibold text-[var(--cli-text)]">
            Onboarding complete!
          </p>
          <HelperText className="mt-1">
            You&apos;re all set. Just tell your Claude what you want.
          </HelperText>
        </div>
      </div>

      <FullCardCopyButton
        text="what can i do with Wonda"
        field="whatCanIDo"
        copiedField={copiedField}
        onCopy={onCopy}
      />
      <HelperText className="text-center">
        Ask your Claude this anytime, and tell it to always use Wonda for
        anything on {memoryPlatforms}.
      </HelperText>

      <Button
        variant="steward"
        size="default"
        className="w-full"
        disabled={isPending}
        onClick={onComplete}
      >
        {isPending ? "Loading..." : "Continue to your account →"}
      </Button>
    </section>
  );
}

function HelperText({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      className={cn("text-sm text-[var(--cli-text-muted)]", className)}
      {...props}
    />
  );
}

function FullCardCopyButton({
  text,
  field,
  copiedField,
  onCopy,
}: {
  text: string;
  field: string;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
}) {
  const isCopied = copiedField === field;
  return (
    <button
      type="button"
      onClick={() => onCopy(text, field)}
      aria-label="Copy prompt"
      className="group relative w-full rounded-xl bg-[var(--cli-bg-elevated)] px-4 py-3 pr-10 text-left transition-colors hover:bg-[var(--cli-bg-hover)]"
    >
      <span className="text-sm leading-relaxed text-[var(--cli-text-secondary)]">
        {text}
      </span>
      <span className="absolute top-3 right-3 text-[var(--cli-text-muted)] opacity-50 transition-opacity group-hover:text-[var(--cli-text)] group-hover:opacity-100">
        {isCopied ? (
          <CheckIcon className="size-4" strokeWidth={1.5} />
        ) : (
          <CopyIcon className="size-4" strokeWidth={1.5} />
        )}
      </span>
    </button>
  );
}

const AGENT_LOGOS = [
  {
    label: "Claude Code",
    icon: "/icons/claude-code-bot.png",
    href: "https://claude.com/download",
  },
  {
    label: "Codex",
    icon: "/icons/codex.svg",
    href: "https://developers.openai.com/codex/app",
  },
  {
    label: "Cursor",
    icon: "/icons/cursor.svg",
    href: "https://cursor.com",
  },
  {
    label: "Openclaw",
    icon: "/icons/openclaw.svg",
    href: "https://openclaw.ai",
  },
];

const CONNECTOR_LOGOS = [
  { src: "/icons/claude.svg" },
  { src: "/icons/openai.svg" },
];

type SetupPath = "cli" | "cowork";

const PATH_TAB_STYLE =
  "flex-1 rounded-md px-3 py-1.5 text-sm leading-normal font-medium text-[var(--cli-text-muted)] transition-colors hover:text-[var(--cli-text)] data-[state=active]:bg-[var(--cli-bg-hover)] data-[state=active]:text-[var(--cli-text)] data-[state=active]:shadow-none";

const AGENT_INSTALL_PROMPT =
  "Hey, please go to wonda.sh and download their CLI and walk me through authentication";

const WELCOME_SUBTITLE =
  "Everything you're doing manually, your agent can now do, in your voice. Describe what you want and Wonda runs it.";

const WELCOME_SUBTITLE_LINKEDIN =
  "Everything you're doing manually on LinkedIn, your agent can now do, in your voice. Describe what you want and Wonda runs it.";

type Capability = { icon: IconSvgElement; label: string };

// The full command set from /wab2's "Why Wonda" capability grid (reads first,
// then writes): everything the agent can drive through the WAB.
const CAPABILITIES: Capability[] = [
  { icon: CursorMagicSelection01Icon, label: "Extract info" },
  { icon: Search01Icon, label: "Search" },
  { icon: News01Icon, label: "Read feed" },
  { icon: UserSearch01Icon, label: "Read profile" },
  { icon: MouseScroll01Icon, label: "Scroll" },
  { icon: UserAdd01Icon, label: "Connect" },
  { icon: Message01Icon, label: "Message" },
  { icon: ThumbsUpIcon, label: "Like" },
  { icon: FavouriteIcon, label: "React" },
  { icon: Comment01Icon, label: "Comment" },
  { icon: Edit02Icon, label: "Post" },
  { icon: Bookmark01Icon, label: "Save" },
];
